import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { Button } from "@material-ui/core";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { Box } from "@mui/material";
import Modal from "@mui/material/Modal";

import { useConfig } from "context/config.context";
import meAction from "store/actions/meAction";
import subscriptionDataAction from "store/actions/subscriptionDataAction";
import businessAction from "store/actions/businessAction";
import businessService from "services/businessService";

import { PrivacyPolicy } from "utils/utils";
import { Chip, Container } from "components";

import mostRequestedBanner from "assets/images/plansScreen/most-requested-banner.svg";
import arrow from "assets/images/plansScreen/VectorArrow.svg";
import checked from "assets/images/plansScreen/VectorChecked.svg";
import "styles/payment/payment.scss";
import { mixpanelTrackEvent } from "utils/mixpanel";
import { sendTokenFirebase, verifyAuthCode } from "services/loginService";

const Payment = () => {
	const [selectedPlan, setSelectedPlan] = useState("Mensal");
	const [expanded, setExpanded] = useState(false);

	const { setTypeAccess, handleLoading } = useConfig();

	const subscriptionData = useSelector((state) => state.subscription_data.subscription_data);
	const subscriptionState = useSelector((state) => state.subscription_current);

	const { business } = useSelector((state) => state.business);
	const { me } = useSelector((state) => state.me);
	const location = useLocation();

	const isCardAndPlanUpdate = location?.state?.isCardAndPlanUpdate;
	const expiration = location?.state?.expiration;

	const pathParams = window.location.pathname.split("/");
	const advancedPlanId = process.env.REACT_APP_ADVANCED_PLAN_ID;
	const beginnerPlanId = process.env.REACT_APP_BEGINNER_PLAN_ID;

	const navigate = useNavigate();
	const dispatch = useDispatch();

	const { token, type, code } = useParams();

	const [auth, setAuth] = useState(getAuth());
	const [open, setOpen] = useState(false);
	const [sortedPlans, setSortedPlans] = useState([]);

	const _date = business?.createdAt ?? moment().format("YYYY-MM-DD");
	const formattedDate = new Date(_date).toISOString();
	const businessId = business ? business.id : 0;

	const handleOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const validateBusinessId = async (id) => {
		if (!id) {
			const data = await businessService.getBusiness();

			if (!data) {
				return 0;
			}

			return data.data[0].id;
		}

		return id;
	};

	const levels = !expiration || subscriptionState.subscription_current?.expiration === null;

	const getCustomToken = useCallback(
		async (code) => {
			try {
				handleLoading(true);
				const result = await verifyAuthCode(code);
				if (result.data?.status === 'OK') {
					const tokenAuth = result.data.message;
					localStorage.setItem("accessToken", tokenAuth);
					await sendTokenFirebase(auth, tokenAuth);
					setAuth(getAuth());
					try {
						const business = await businessService.getBusiness();
						if (business.data) {
							dispatch({ type: "UPDATE_BUSINESS", payload: business.data[0] });
							dispatch(subscriptionDataAction.getSubscriptionData(formattedDate, business.data[0].id, levels));
							dispatch(subscriptionDataAction.getSubscriptionCurrent(business.data[0].id));
						}
						localStorage.setItem("logged", "true");
						const onSubscribe = onAuthStateChanged(auth, (user) => {
							if (user) {
								const tokenAuth = user.accessToken;
								const config = {
									headers: {
										Authorization: "Bearer " + tokenAuth,
									},
								};

								dispatch(meAction.getMe(config));
								dispatch(businessAction.getBusiness(business?.id));
							}

						});
						onSubscribe();
						handleLoading(false);
					} catch (err) {
						console.log("🚀 ~ getCustomToken 1 ~ error:", err);
					}
				} else {
					navigate("/subscription/onboarding");
				}
				handleLoading(false);
				mixpanelTrackEvent('Clicou_Mensagem_Pix', me || {}, business || {});
			} catch (error) {
				console.log("🚀 ~ getCustomToken error:", error);
			}
		},
		[code, dispatch]
	);


	useEffect(() => {
		if (code) return;
		setTypeAccess(type ?? "schedule");
		const unsubscribe = onAuthStateChanged(auth, (user) => {
			if (!pathParams || pathParams.length < 3) {
				if (user) {
					const tokenAuth = user.accessToken;
					const config = {
						headers: {
							Authorization: "Bearer " + tokenAuth,
						},
					};

					dispatch(meAction.getMe(config));
					dispatch(businessAction.getBusiness(business?.id));
				} else {
					setAuth(getAuth());
					if (location.pathname === "/menu") {
						setTypeAccess("renew");
						if (!business) navigate("/login");
					} else if (type) {
						if (!business) navigate("/login");
					} else {
						setTypeAccess("onboarding");
						navigate("/welcome");
					}
				}
			} else if (pathParams && pathParams.length > 3) {
				const tokenAuth = pathParams[3];
				localStorage.setItem("accessToken", tokenAuth);
				dispatch(meAction.getMe());
				dispatch(businessAction.getBusiness(business?.id));
			}
		});

		return () => unsubscribe();
	}, [auth, token, type]);

	useEffect(() => {
		if (code) {
			getCustomToken(code);
			return;
		}
		const fetchSubscriptionData = async () => {
			if (businessId === 0) {
				try {
					if (!auth) {
						const authInstance = getAuth();
						setAuth(authInstance);
					}

					if (auth?.currentUser || pathParams) {
						const id = await validateBusinessId(businessId);

						dispatch(subscriptionDataAction.getSubscriptionData(formattedDate, id, levels));
						dispatch(subscriptionDataAction.getSubscriptionCurrent(id));
					}
				} catch (error) {
					console.error("Erro ao buscar os dados da assinatura:", error);
					navigate("/login");
				}
			} else if ((auth?.currentUser || pathParams) && businessId) {
				try {
					await dispatch(subscriptionDataAction.getSubscriptionData(formattedDate, businessId, levels));
					await dispatch(subscriptionDataAction.getSubscriptionCurrent(businessId));
				} catch (error) {
					console.error("Erro ao buscar os dados da assinatura:", error);
					navigate("/login");
				}
			}
		};
		mixpanelTrackEvent('Abriu_Tela_Planos_Web', me || {}, business || {});
		setTimeout(() => {
			fetchSubscriptionData();
		}, 1000);
	}, []);

	const handlePeriodClick = (plan) => {
		setSelectedPlan(plan === selectedPlan ? null : plan);
	};

	const handleClick = (selectedPlan) => {
		navigate("/subscription/methods", { state: { id: selectedPlan?.id, isCardAndPlanUpdate: isCardAndPlanUpdate } });
	};

	function capitalizeFirstLetter(string) {
		return string.charAt(0).toUpperCase() + string.slice(1);
	}

	const filteredPlans = (plans) => {
		if (location.pathname === "/subscription/renew") {
			return plans.filter((plan) => plan.id !== beginnerPlanId);
		}

		return plans;
	};

	useEffect(() => {
		if (subscriptionData) {
			const sorted = subscriptionData.sort((a, b) => a.mountPrice - b.mountPrice);
			setSortedPlans(filteredPlans(sorted));
		}
	}, [subscriptionData]);

	const style = {
		position: "absolute",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		width: 400,
		height: "80%",
		bgcolor: "background.paper",
		border: "none",
		boxShadow: 24,
		borderRadius: "12px",
		pt: 2,
		px: 4,
		pb: 3,
		overflow: "auto",
		"&:focus": {
			outline: "none",
		},
	};

	const subtitles = {
		iniciante: "Descubra o básico com nosso plano Iniciante.",
		básico: "Mais recursos, mais possibilidades.",
		avançado: "Máxima eficiência com recursos avançados.",
		profissional: "O pacote completo para profissionais.",
		essencial: "Comece a contar com nossa Assistente Virtual.",
	};

	return (
		<Container style={{ gap: "16px" }}>
			<div style={{ display: "flex" }}>
				<h1 className="TitlePlans">Selecione seu Plano</h1>
				<Chip help={true} customStyle={{ marginTop: "1rem", borderRadius: "1rem" }} />
			</div>

			<section className="periodBox">
				<button
					className={selectedPlan === "Mensal" ? "buttonActivated" : "buttonDefault"}
					onClick={() => handlePeriodClick("Mensal")}>
					Mensal
				</button>
				<button
					className={selectedPlan === "Anual" ? "buttonActivated" : "buttonDefault"}
					onClick={() => handlePeriodClick("Anual")}>
					Anual
				</button>
			</section>

			<div className="planContainer">
				{sortedPlans
					.filter((plan) => {
						return selectedPlan === "Mensal" ? plan.daysRef === 30 : plan.daysRef === 365;
					})
					.map((plan) => (
						<div key={plan.id} className={plan.id === advancedPlanId ? "borderAdvanced" : ""}>
							<div className="planBox" style={{ marginRight: window.innerWidth > window.innerHeight ? "8px" : "0" }}>
								{plan.id === advancedPlanId && selectedPlan === "Mensal" && (
									<div className="advancedPlanBox">
										<img src={mostRequestedBanner} alt="banner-img" />
									</div>
								)}
								<h3 className="planTitle">{capitalizeFirstLetter(plan.title)}</h3>
								<p className="planSubs">{subtitles[plan.title]}</p>
								<Accordion
									expanded={expanded === plan.title}
									onChange={(event, isExpanded) => {
										event.stopPropagation();
										setExpanded(isExpanded ? plan.title : "");
									}}>
									<AccordionSummary
										className="accordionContainer"
										expandIcon={<img src={arrow} alt="arrowImg" />}
										aria-controls="panel-content"
										sx={{
											padding: 0,
											width: "100%",
										}}
										id="panel-header">
										<div className="detailsContainer">
											<p className="planPrice">
												{(plan.mountPrice / 100).toLocaleString("pt-BR", {
													style: "currency",
													currency: "BRL",
												})}
												<span>{selectedPlan === "Mensal" ? "/mês" : "/ano"}</span>
											</p>
											<p className="planDetails">Detalhes</p>
										</div>
									</AccordionSummary>
									<AccordionDetails
										style={{ minWidth: "-webkit-fill-available", backgroundColor: "white", padding: "0" }}>
										<p className="planDescription">
											{plan.description.split("\n").map((line, index) => {
												const splitted = line.split(/(assistente virtual)/gi);
												return (
													<React.Fragment key={index}>
														<img src={checked} alt="checkedIcon" style={{ marginRight: "0.5rem" }} />
														<span>
															{splitted.map((part, i) => {
																return part.toLowerCase() === "assistente virtual" ? (
																	<strong key={i}>{part}</strong>
																) : (
																	<React.Fragment key={i}>{part}</React.Fragment>
																);
															})}
														</span>
														<br />
													</React.Fragment>
												);
											})}
										</p>
									</AccordionDetails>
								</Accordion>
								<div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
									<Button
										variant="contained"
										color="primary"
										fullWidth
										style={{
											backgroundColor: "#3688EB",
											borderRadius: "8px",
											textTransform: "none",
											width: "70%",
											fontFamily: "Poppins",
										}}
										onClick={() => handleClick(plan)}>
										Contratar Plano
									</Button>
								</div>
							</div>
						</div>
					))}
			</div>

			<p className="privacyPolicy">
				Ao clicar em “Contratar Plano” você concorda com nossa{" "}
				<span style={{ color: "#3688EB", cursor: "pointer" }} onClick={handleOpen}>
					Política de Privacidade
				</span>
				.
			</p>

			<Modal
				open={open}
				onClose={handleClose}
				aria-labelledby="parent-modal-title"
				aria-describedby="parent-modal-description">
				<Box sx={{ ...style, width: 400, whiteSpace: "pre-line" }}>
					<h2 id="parent-modal-title">Política de Privacidade</h2>
					<p id="parent-modal-description">{PrivacyPolicy}</p>
				</Box>
			</Modal>
		</Container>
	);
};

export default Payment;
